import { createContext, useContext, useRef, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const ScrollContext = createContext();

// Height of your fixed/sticky header in px — adjust to match your actual navbar
const HEADER_OFFSET = 80;

export const ScrollProvider = ({ children }) => {
  const [activeSection, setActiveSection] = useState('banner');
  const navigate = useNavigate();
  const location = useLocation();

  // Guards the observer from overwriting activeSection while we're
  // programmatically scrolling (click nav / hash nav).
  const isProgrammaticScroll = useRef(false);
  const programmaticScrollTimeout = useRef(null);

  const scrollToElement = useCallback((sectionId) => {
    const element = document.getElementById(sectionId);
    if (!element) return false;

    isProgrammaticScroll.current = true;
    clearTimeout(programmaticScrollTimeout.current);

    const top =
      element.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;

    window.scrollTo({ top, behavior: 'smooth' });

    // Fallback safety-net in case 'scrollend' isn't supported / doesn't fire
    programmaticScrollTimeout.current = setTimeout(() => {
      isProgrammaticScroll.current = false;
    }, 1000);

    const handleScrollEnd = () => {
      isProgrammaticScroll.current = false;
      clearTimeout(programmaticScrollTimeout.current);
      window.removeEventListener('scrollend', handleScrollEnd);
    };
    window.addEventListener('scrollend', handleScrollEnd, { once: true });

    return true;
  }, []);

  const scrollToSection = useCallback(
    (sectionId) => {
      setActiveSection(sectionId);

      if (location.pathname === '/') {
        scrollToElement(sectionId);
      } else {
        navigate(`/#${sectionId}`);
      }
    },
    [location.pathname, navigate, scrollToElement]
  );

  return (
    <ScrollContext.Provider
      value={{
        activeSection,
        setActiveSection,
        scrollToSection,
        scrollToElement,
        isProgrammaticScroll,
      }}
    >
      {children}
    </ScrollContext.Provider>
  );
};

export const useScroll = () => useContext(ScrollContext);